import { createSelector } from "@reduxjs/toolkit";
import memoize from "memoize-one";

import {
    IFormUUID,
    IReviewsProductID,
    IReviewsProductTypeID,
    ProductUUID,
} from "../../models/nominals";
import {
    IReviewsProduct,
    IReviewsProductVariant,
    IReviewsProductVariantChoices,
} from "../../models/reviews";
import { IRootState } from "../../store/reducers.interfaces";
import { notEmpty, unique } from "../../utils/functional";
import { parseFacetValues } from "../../utils/reviews";
import { NOT_SURE_VARIANT_CHOICE_SLUG, WriteReviewFormMode } from "./constants";
import {
    defaultReviewTemplate,
    defaultState,
    getDefaultWriteReviewFormState,
} from "./defaults";
import {
    ICustomerInformationState,
    IReduxState,
    IReviewFormProductTemplatePair,
    IWriteReviewFormState,
} from "./models";

export const getDefaultFilterOptions = (
    products: ReadonlyArray<IReviewsProduct>,
) => {
    // Select all the products types
    const productTypeIDs = products
        .map((p) => {
            return p.product_type;
        })
        .filter(notEmpty)
        .filter(unique);
    // Select all the products
    const productIDs = products
        .map((p) => p.id)
        .filter(notEmpty)
        .filter(unique);
    // Select all the product variants
    const variantIDs = products
        .reduce<IReviewsProductVariant[]>(
            (memo, p) => memo.concat(p.variants),
            [],
        )
        .map((v) => v.id)
        .filter(notEmpty)
        .filter(unique);
    return {
        productTypeIDs,
        productIDs,
        variantIDs,
    };
};

export const getWriteReviewFormHTMLID = (formUUID: IFormUUID): string => {
    return `write-review-form-${formUUID}`;
};

export const getProductByUUID = (
    state: IReduxState,
    props: {
        productUUID: ProductUUID;
    },
): IReviewsProduct | null => {
    return (
        state.data.products.find((p) => p.uuid === props.productUUID) ?? null
    );
};

export const getWriteReviewFormState = memoize(
    (
        state: IReduxState,
        props: {
            formUUID: IFormUUID;
        },
    ): IWriteReviewFormState => {
        return (
            state.ui.writeReviewForms[String(props.formUUID)] ??
            getDefaultWriteReviewFormState()
        );
    },
);

export const getCustomerInformationState = (
    state: IReduxState,
): ICustomerInformationState => {
    return state.ui.customerInformation;
};

const getWriteReviewTemplates = (
    state: IReduxState,
): readonly IReviewFormProductTemplatePair[] => {
    return state.data.writeReviewTemplates;
};

export const getWriteReviewFormMode = createSelector(
    [getWriteReviewFormState],
    (formState): WriteReviewFormMode => {
        return formState.mode;
    },
);

export const getWriteReviewFormSelectedProductID = createSelector(
    [getWriteReviewFormState],
    (formState): IReviewsProductID | null => {
        return formState.selectedProductID;
    },
);

export const getWriteReviewFormProducts = (
    state: IReduxState,
    props: {
        displayedProductTypeIDWhitelist?: readonly IReviewsProductTypeID[];
    },
): readonly IReviewsProduct[] => {
    if (!props.displayedProductTypeIDWhitelist) {
        return state.data.products;
    }
    return state.data.products.filter((p) => {
        return (
            p.product_type &&
            props.displayedProductTypeIDWhitelist!.includes(p.product_type)
        );
    });
};

export const getWriteReviewFormSelectedProduct = createSelector(
    [getWriteReviewFormProducts, getWriteReviewFormSelectedProductID],
    (products, selectedProductID): IReviewsProduct | null => {
        const selectedProduct = products.find((p) => {
            return p.id === selectedProductID;
        });
        return selectedProduct ?? null;
    },
);

export const getWriteReviewFormSelectedProductTypeID = createSelector(
    [getWriteReviewFormSelectedProduct],
    (product): IReviewsProductTypeID | null => {
        return product ? product.product_type : null;
    },
);

export const getWriteReviewFormVariantChoices = createSelector(
    [getWriteReviewFormSelectedProduct],
    (product): IReviewsProductVariantChoices[] => {
        if (product && product.variants.length) {
            return [null, NOT_SURE_VARIANT_CHOICE_SLUG, ...product.variants];
        } else {
            return [];
        }
    },
);

export const getWriteReviewFormSelectedVariantSlug = createSelector(
    [getWriteReviewFormVariantChoices, getWriteReviewFormState],
    (variants, formState): string => {
        if (variants.length <= 0) {
            return "";
        }
        const slugs = variants.map((v: IReviewsProductVariantChoices) => {
            if (v === NOT_SURE_VARIANT_CHOICE_SLUG) {
                return NOT_SURE_VARIANT_CHOICE_SLUG;
            } else if (v !== null) {
                return v.slug;
            }
            return "";
        });
        return slugs.includes(formState.selectedVariant)
            ? formState.selectedVariant
            : slugs[0];
    },
);

export const getWriteReviewFormSelectedVariant = createSelector(
    [getWriteReviewFormVariantChoices, getWriteReviewFormSelectedVariantSlug],
    (variants, slug): IReviewsProductVariantChoices => {
        return (
            variants.find(
                (v: IReviewsProductVariantChoices) =>
                    v && v !== NOT_SURE_VARIANT_CHOICE_SLUG && v.slug === slug,
            ) ?? null
        );
    },
);

export const getWriteReviewTemplateForForm = createSelector(
    [getWriteReviewTemplates, getWriteReviewFormSelectedProductID],
    (templatePairs, selectedProductID): IReviewFormProductTemplatePair => {
        const formTemplate = templatePairs.find(
            ({ productID }) => productID === selectedProductID,
        );
        return (
            formTemplate ?? {
                productID: null,
                template: defaultReviewTemplate,
            }
        );
    },
);

const selectTextFacetValues = (
    rootState: IRootState,
): string | number | undefined => {
    const state = rootState.reviews || defaultState;
    return state.ui.facetValues.text;
};

export const selectTextFilterQueries = createSelector(
    [selectTextFacetValues],
    (text): readonly string[] => parseFacetValues(text),
);
